import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonGroup, MenuItem, Stack, TextField } from '@mui/material';
import { PamGrid } from '@1823-partners/core';
import StatusCards from './StatusCards';
import StatusChip from './StatusChip';
import TradeDetailDialog from './TradeDetailDialog';
import { STATUS, STATUS_ORDER, TOLERANCE_VAL } from '../../utils/derivativeReconConstants';
import { fmt } from '../../utils/derivativeReconFormat';
import { wireIdsFor } from '../../utils/derivativeReconLogic';

function parseD(s) {
  if (!s) return null;
  const p = String(s).split('/');
  if (p.length === 3) return new Date(+p[2], +p[0] - 1, +p[1]);
  return new Date(s) || null;
}

function inRange(ds, fromD, toD) {
  const d = parseD(ds);
  if (!d) return true;
  if (fromD && d < fromD) return false;
  if (toD && d > toD) return false;
  return true;
}

const SOURCES = ['All', 'Positions', 'Premiums', 'Payoffs'];

/**
 * Find the custody wires a break settled on, strictly within the break's own
 * flow type: the index key first, then a scan of the cash results for wires
 * whose flow of that type carried this trade ID. A trade normally has both a
 * premium and a payoff, so accepting the other type's wire would present an
 * unmatched flow as settled.
 */
function resolveWires(selected, selectedType, wires, tradeWireIndex, cashResults) {
  if (!selected || selectedType === 'Positions') return [];
  const flowType = selectedType === 'Premiums' ? 'Premium' : 'Payoff';
  const tid = String(selected.id || '').trim();
  // One trade of one type can settle across several dates, so this is a list.
  const found = wireIdsFor(tradeWireIndex, flowType, tid)
    .map((wid) => wires[wid])
    .filter(Boolean);
  if (found.length) return found;
  const scanned = [];
  for (const r of cashResults) {
    if (!r || !r.wireId || !r.mil || r.mil.type !== flowType) continue;
    const ids = (r.mil.tradeIds || []).map((x) => String(x).trim());
    if (ids.includes(tid) && wires[r.wireId] && !scanned.includes(wires[r.wireId])) {
      scanned.push(wires[r.wireId]);
    }
  }
  return scanned;
}

export default function BreaksView({
  posResults,
  premResults,
  payResults,
  dateFrom,
  dateTo,
  wires = {},
  tradeWireIndex = {},
  cashResults = [],
}) {
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selected, setSelected] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const fromD = dateFrom
    ? (() => { const p = dateFrom.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); })()
    : null;
  const toD = dateTo
    ? (() => { const p = dateTo.split('-'); const d = new Date(+p[0], +p[1] - 1, +p[2]); d.setHours(23, 59, 59, 999); return d; })()
    : null;

  const allBreaks = useMemo(() => {
    const rows = [];
    (posResults || []).forEach((r) => {
      if (r.status === 'MATCHED') return;
      if (!inRange(r.milTrade, fromD, toD)) return;
      rows.push({ ...r, _source: 'Positions', _df: r.milTrade || r.cwTrade });
    });
    (premResults || []).forEach((r) => {
      if (r.status === 'MATCHED') return;
      if (!inRange(r.settle, fromD, toD)) return;
      rows.push({ ...r, _source: 'Premiums', _df: r.settle });
    });
    (payResults || []).forEach((r) => {
      if (r.status === 'MATCHED') return;
      if (!inRange(r.settle, fromD, toD)) return;
      rows.push({ ...r, _source: 'Payoffs', _df: r.settle });
    });
    return rows;
  }, [posResults, premResults, payResults, fromD, toD]);

  const filtered = useMemo(() => {
    let r = allBreaks;
    if (filterSource !== 'All') r = r.filter((x) => x._source === filterSource);
    if (filterStatus !== 'All') r = r.filter((x) => x.status === filterStatus);
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(
        (x) =>
          (x.id || '').toLowerCase().includes(s) ||
          (x.cpName || '').toLowerCase().includes(s) ||
          (x.entity || '').toLowerCase().includes(s),
      );
    }
    return r;
  }, [allBreaks, filterSource, filterStatus, search]);

  const counts = useMemo(() => {
    const c = {};
    Object.keys(STATUS).forEach((s) => { c[s] = 0; });
    filtered.forEach((r) => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [filtered]);

  const columnDefs = useMemo(
    () => [
      {
        field: '_source', headerName: 'Source', width: 110,
        cellRenderer: (p) => {
          const colors = { Positions: '#5b7fff', Premiums: '#38bdf8', Payoffs: '#fbbf24' };
          const c = colors[p.value] || '#94a3b8';
          return (
            <Box component="span" sx={{ bgcolor: `${c}22`, color: c, border: `1px solid ${c}66`, px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 700 }}>
              {p.value}
            </Box>
          );
        },
      },
      { field: 'id', headerName: 'Trade ID', width: 130 },
      { field: 'entity', headerName: 'Entity', width: 90 },
      { field: 'cpName', headerName: 'Broker', width: 150 },
      { field: '_df', headerName: 'Date', width: 110 },
      { headerName: 'Mil Amount', width: 130, type: 'numericColumn', valueGetter: (p) => p.data._source === 'Positions' ? p.data.milVal : p.data.milTotal, valueFormatter: (p) => fmt(p.value) },
      { headerName: 'CW Amount', width: 130, type: 'numericColumn', valueGetter: (p) => p.data._source === 'Positions' ? p.data.cwVal : p.data.cwTotal, valueFormatter: (p) => fmt(p.value) },
      { headerName: 'Difference', width: 130, type: 'numericColumn', valueGetter: (p) => p.data._source === 'Positions' ? p.data.valDiff : p.data.diff, valueFormatter: (p) => fmt(p.value), cellStyle: (p) => { if (p.value == null) return {}; return { color: Math.abs(p.value) > TOLERANCE_VAL ? '#f87171' : '#34d399', fontWeight: 700 }; } },
      { field: 'status', headerName: 'Status', width: 170, cellRenderer: (p) => <StatusChip status={p.value} /> },
    ],
    [],
  );

  return (
    <Box>
      <StatusCards statuses={STATUS} statusOrder={STATUS_ORDER} counts={counts} total={filtered.length} />
      <Stack direction="row" spacing={1} mb={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField size="small" placeholder="Search trade ID, broker, entity…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
        <ButtonGroup size="small">
          {SOURCES.map((s) => (
            <Button key={s} variant={filterSource === s ? 'contained' : 'outlined'} onClick={() => setFilterSource(s)}>{s}</Button>
          ))}
        </ButtonGroup>
        <TextField size="small" select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="All">All Statuses</MenuItem>
          {STATUS_ORDER.filter((s) => s !== 'MATCHED').map((s) => <MenuItem key={s} value={s}>{STATUS[s].label}</MenuItem>)}
        </TextField>
        {(search || filterSource !== 'All' || filterStatus !== 'All') && (
          <Button size="small" color="error" variant="outlined" onClick={() => { setSearch(''); setFilterSource('All'); setFilterStatus('All'); }}>✕ Clear</Button>
        )}
      </Stack>
      <Box sx={{ height: 600, width: '100%' }}>
        <PamGrid
          rowData={filtered}
          columnDefs={columnDefs}
          getRowId={(data) => `${data?._source}-${data?.id}-${data?.status}`}
          onRowClicked={(e) => { setSelected(e.data); setSelectedType(e.data._source); }}
          defaultColDef={{ sortable: true, resizable: true, filter: true, wrapHeaderText: true, autoHeaderHeight: true }}
          enableShowToolbar={false}
        />
      </Box>
      <TradeDetailDialog
        trade={selected}
        type={selectedType || 'Premiums'}
        wires={resolveWires(selected, selectedType, wires, tradeWireIndex, cashResults)}
        onClose={() => { setSelected(null); setSelectedType(null); }}
      />
    </Box>
  );
}
